import React, { useMemo } from 'react'
import { Star } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { getMenuLabel } from '../lib/menu'
import MenuIcon from './MenuIcon'

const USER_BLOCK_IDS = new Set(['dashboard', 'sales', 'customer', 'design', 'work', 'admin'])

function isPathActive(pathname, itemPath) {
  if (!itemPath) return false
  const pathOnly = itemPath.split('?')[0]
  if (pathOnly === '/') return pathname === '/'
  return pathname === pathOnly || pathname.startsWith(`${pathOnly}/`)
}

function splitOverviewBlocks(blocks) {
  const userBlocks = []
  const operatorBlocks = []
  ;(blocks || []).forEach((block) => {
    if (USER_BLOCK_IDS.has(block.id)) userBlocks.push(block)
    else operatorBlocks.push(block)
  })
  return { userBlocks, operatorBlocks }
}

function MenuOverviewLink({ item, pathname, onItemClick, favorite, favoritePending, onToggleFavorite }) {
  const active = isPathActive(pathname, item.path)
  return (
    <div className={`flex items-center transition-colors ${active ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
      <Link
        to={item.path}
        onClick={onItemClick}
        className={`flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-base font-medium ${
          active ? 'text-slate-950' : 'text-slate-800'
        }`}
      >
        <MenuIcon menuItemId={item.id} emoji={item.icon} className={active ? 'text-brand-navy' : 'text-slate-500'} size={20} />
        <span className="min-w-0 leading-snug">{getMenuLabel(item)}</span>
      </Link>
      <button
        type="button"
        className={`mr-2 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded transition-colors disabled:cursor-wait disabled:opacity-50 ${
          favorite
            ? 'text-amber-500 hover:bg-amber-50'
            : 'text-slate-400 hover:bg-slate-100 hover:text-amber-500'
        }`}
        aria-label={`${getMenuLabel(item)} 즐겨찾기 ${favorite ? '해제' : '추가'}`}
        aria-pressed={favorite}
        title={favorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        disabled={favoritePending || !item.menuKey}
        onClick={() => onToggleFavorite?.(item, favorite)}
      >
        <Star className={`h-5 w-5 ${favorite ? 'fill-current' : ''}`} aria-hidden="true" />
      </button>
    </div>
  )
}

function MenuOverviewColumn({
  block,
  pathname,
  onItemClick,
  favoriteMenuIds,
  pendingMenuId,
  onToggleFavorite,
}) {
  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-base font-bold text-slate-950">
        {block.label}
      </div>
      <ul className="divide-y divide-slate-100">
        {block.items.map((item) => (
          <li key={item.id}>
            <MenuOverviewLink
              item={item}
              pathname={pathname}
              onItemClick={onItemClick}
              favorite={favoriteMenuIds.has(item.id)}
              favoritePending={pendingMenuId === item.id}
              onToggleFavorite={onToggleFavorite}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

function MenuOverviewColumnGrid({
  blocks,
  pathname,
  onItemClick,
  favoriteMenuIds,
  pendingMenuId,
  onToggleFavorite,
  columnsClass = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
}) {
  if (!blocks.length) return null
  return (
    <div className={`grid gap-4 ${columnsClass}`}>
      {blocks.map((block) => (
        <MenuOverviewColumn
          key={block.id}
          block={block}
          pathname={pathname}
          onItemClick={onItemClick}
          favoriteMenuIds={favoriteMenuIds}
          pendingMenuId={pendingMenuId}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </div>
  )
}

export default function MenuOverviewList({
  blocks,
  onItemClick,
  favoriteMenuIds = new Set(),
  pendingMenuId = null,
  onToggleFavorite,
}) {
  const { pathname } = useLocation()
  const { userBlocks, operatorBlocks } = useMemo(() => splitOverviewBlocks(blocks), [blocks])

  if (!userBlocks.length && !operatorBlocks.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500">
        표시할 메뉴가 없습니다.
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <MenuOverviewColumnGrid
        blocks={userBlocks}
        pathname={pathname}
        onItemClick={onItemClick}
        favoriteMenuIds={favoriteMenuIds}
        pendingMenuId={pendingMenuId}
        onToggleFavorite={onToggleFavorite}
        columnsClass="grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
      />

      {operatorBlocks.length ? (
        <div className="space-y-4">
          <h2 className="text-sm font-bold text-brand-navy">운영자 메뉴</h2>
          <MenuOverviewColumnGrid
            blocks={operatorBlocks}
            pathname={pathname}
            onItemClick={onItemClick}
            favoriteMenuIds={favoriteMenuIds}
            pendingMenuId={pendingMenuId}
            onToggleFavorite={onToggleFavorite}
            columnsClass="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          />
        </div>
      ) : null}
    </div>
  )
}